import React, { useEffect } from 'react';
import PageHeader from '@atlassian/jira-common-components-page-header';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import ReportErrors from '@atlassian/jira-errors-handling/src/utils/reporting-error-boundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { PermissionGenericError } from '@atlassian/jira-standard-errors/src/common/utils/permission-generic-error/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { TRASHED_STATUS } from '../common/constants';
import TableController from '../controllers/table-controller';
import Filters from './filters';
import messages from './messages';
import PlansTable from './plans-table';
import type { Props } from './types';

export const InactivePlansPage = (props: Props) => {
	const { status } = props;

	const isAdmin = useIsAdmin();
	const { formatMessage } = useIntl();

	useEffect(() => {
		if (!isAdmin) {
			const permissionError = new PermissionGenericError('User is not Jira Admin');
			fireErrorAnalytics({
				meta: {
					id: `${status}PermissionError`,
					packageName: 'jiraInactivePlansDirectoryBase',
				},
				error: permissionError,
				attributes: {
					status,
				},
			});
			throw permissionError;
		}
	}, [isAdmin, status]);

	const pageHeader =
		status === TRASHED_STATUS ? messages.headerCaptionForTrash : messages.headerCaptionForArchive;

	return (
		<ReportErrors id={`${status}PlansDirectory`} packageName="jiraInactivePlansDirectoryBase">
			<SubProductUpdater subProduct="software" />
			<PageHeader truncateTitle>{formatMessage(pageHeader)}</PageHeader>
			<TableController status={status}>
				{({
					isLoading,
					data,
					page,
					limit,
					onPageChange,
					onSort,
					sortField,
					sortDirection,
					refetchPlans,
					onFilterChange,
					filter,
				}) => (
					<>
						<Filters
							onFilterChange={onFilterChange}
							filter={filter}
							{...(fg('jira-a11y-violations-label-trashed-plans-directory') && {
								status,
							})}
						/>
						<PlansTable
							isLoading={isLoading}
							data={data}
							page={page}
							limit={limit}
							onPageChange={onPageChange}
							onSort={onSort}
							sortField={sortField}
							refetchPlans={refetchPlans}
							sortDirection={sortDirection}
							status={status}
						/>
					</>
				)}
			</TableController>
		</ReportErrors>
	);
};
